import { useEffect, useMemo, useRef, useState } from "react";
import "./DeleteModal.css";
import { Box } from "@mui/material";
import Title from "../title/Title";
import {
  PrimaryButton,
  SecondaryButton,
  TextButton,
} from "../buttonComponent/ButtonComponent";
import ModalCloseButton from "./ModalCloseButton";
import Institution from "./img/institution.svg";
import TextInput from "../textInput/TextInput";
import { useAuth } from "../../config/utilities/useAuth";
import { APICreateInstitution } from "../../config/utilities/APIs/masterAdmin/masterAdminApi";
import { APIGetAdministratorByFirebaseToken } from "../../config/utilities/APIs/adminApi";
import Dropdown from "../dropdown/Dropdown";
import CustomDatePicker from "../datePicker/DatePicker";
import dayjs from "dayjs";
import { ENABLE_INSTITUTION_SUBSCRIPTION } from "../../config/featureFlags";

const DEFAULT_TRIP_TYPES = ["AM", "PM", "ECA"];

const SUBSCRIPTION_PLAN_OPTIONS = [
  { label: "Trial", value: "TRIAL" },
  { label: "Premium", value: "PREMIUM" },
  { label: "Standard", value: "STANDARD" },
  { label: "Restricted", value: "RESTRICTED" },
];

const getDefaultValidUntil = () =>
  dayjs().add(1, "year").format("YYYY-MM-DD");

const getDefaultStartAt = () => dayjs().format("YYYY-MM-DD");

const getDefaultSubscriptionFields = () => ({
  subscriptionStartAt: getDefaultStartAt(),
  subscriptionValidUntil: getDefaultValidUntil(),
  subscriptionPlan: "STANDARD",
  subscriptionGracePeriodDays: "7",
});

const getDefaultFormState = () => ({
  institutionId: "",
  institutionName: "",
  timezone: "",
  customTripTypes: [],
  ...getDefaultSubscriptionFields(),
});

const getDefaultErrorState = () => ({
  institutionName: false,
  institutionId: false,
  timezone: false,
  subscriptionStartAt: false,
  subscriptionValidUntil: false,
  subscriptionGracePeriodDays: false,
});

const STEP_SUBTITLES = {
  1: "Enter the institution's basic details",
  2: "Configure the subscription plan and dates",
  3: "Set up trip types for routes and schedules",
};

const getWizardSteps = () =>
  ENABLE_INSTITUTION_SUBSCRIPTION
    ? [
        { id: 1, panel: 1, label: "Details" },
        { id: 2, panel: 2, label: "Subscription" },
        { id: 3, panel: 3, label: "Trip types" },
      ]
    : [
        { id: 1, panel: 1, label: "Details" },
        { id: 2, panel: 3, label: "Trip types" },
      ];

const isAlphanumericWithSpaces = (str) => /^[a-zA-Z0-9 ]+$/.test(str);
const isAlphanumericNoSpaces = (str) => /^[a-zA-Z0-9]+$/.test(str);

const toSubscriptionISODate = (dateValue) => {
  if (!dateValue) return null;
  return new Date(`${dateValue}T00:00:00.000Z`).toISOString();
};

const FIELD_WIDTH = "100%";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
  width: "min(28rem, calc(100vw - 2rem))",
  overflow: "hidden",
  maxHeight: "92vh",
};

export default function CreateInstitutionModal({
  isOpen,
  onClose,
  setShowSnackbar,
  setSnackbarSubtitle,
  setSnackbarTitle,
  setSnackbarType,
  loading,
  setLoading,
  fetchInstitutions,
  onCreateInstitution,
}) {
  const { userId, token, setPrivilegeSelection } = useAuth();

  const [input, setInput] = useState(getDefaultFormState);
  const [error, setError] = useState(getDefaultErrorState);
  const [keyUp, setKeyUp] = useState("", false);
  const [currentStep, setCurrentStep] = useState(1);
  const [customTripTypeTouched, setCustomTripTypeTouched] = useState([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);

  const formatLabel = (tz) => {
    const now = new Date();

    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    }).formatToParts(now);

    const offset =
      parts
        .find((p) => p.type === "timeZoneName")
        ?.value.replace("GMT", "UTC") || "UTC";

    const prettyName = tz.replace(/_/g, " ");

    return `(${offset}) ${prettyName}`;
  };

  const timezoneOptions = Intl.supportedValuesOf("timeZone")
    .map((tz) => ({
      label: formatLabel(tz),
      value: tz,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (!isOpen) return;

    setInput(getDefaultFormState());
    setError(getDefaultErrorState());
    setCurrentStep(1);
    setCustomTripTypeTouched([]);
    setKeyUp("");
    setIsSubmitDisabled(true);
  }, [isOpen]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [isOpen, currentStep]);

  const isStep1Valid = () => {
    const id = input.institutionId?.trim();
    const name = input.institutionName?.trim();
    const timezone = input.timezone?.trim();

    return (
      id !== "" &&
      id.length <= 60 &&
      isAlphanumericNoSpaces(id) &&
      name !== "" &&
      name.length <= 60 &&
      isAlphanumericWithSpaces(name) &&
      timezone !== ""
    );
  };

  const isStep2Valid = () => {
    const startAt = (input.subscriptionStartAt || "").trim();
    const validUntil = (input.subscriptionValidUntil || "").trim();
    const gracePeriodDays = (input.subscriptionGracePeriodDays || "").trim();

    return (
      startAt !== "" &&
      validUntil !== "" &&
      !dayjs(validUntil).isBefore(dayjs(startAt), "day") &&
      gracePeriodDays !== "" &&
      /^\d+$/.test(gracePeriodDays) &&
      input.subscriptionPlan !== ""
    );
  };

  const isStep3Valid = () =>
    (input.customTripTypes || []).every(
      (tripType) =>
        tripType.trim() !== "" && isAlphanumericWithSpaces(tripType.trim()),
    );

  const wizardSteps = useMemo(() => getWizardSteps(), []);
  const activeWizardStep = wizardSteps[currentStep - 1];
  const activePanel = activeWizardStep?.panel ?? 1;

  const isCurrentStepValid = () => {
    if (activePanel === 1) return isStep1Valid();
    if (activePanel === 2) return isStep2Valid();
    return isStep3Valid();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedInput = { ...input, [name]: value };
    setInput(updatedInput);
  };

  const handleReset = async () => {
    setInput(getDefaultFormState());
    setError(getDefaultErrorState());
    setKeyUp("");
    setCurrentStep(1);
    setCustomTripTypeTouched([]);
    setLoading(false);
    setIsSubmitDisabled(true);
    onClose();
  };

  const updateUserPrivileges = async () => {
    try {
      const adminDetails = await APIGetAdministratorByFirebaseToken(
        token,
        setLoading,
      );
      if (adminDetails) {
        const privileges = adminDetails.privileges || [];
        setPrivilegeSelection(privileges);
      } else {
        throw new Error(
          adminDetails?.response?.data?.message || "Failed to fetch privileges",
        );
      }
    } catch (error) {
      console.error("Error updating user privileges:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setLoading(true);

    const formattedInput = {
      userId: userId,
      institutionId: input.institutionId,
      institutionName: input.institutionName,
      timezone: input.timezone,
      tripTypes: [
        ...DEFAULT_TRIP_TYPES,
        ...(input.customTripTypes || []).map((tripType) => tripType.trim()),
      ].filter(Boolean),
      ...(ENABLE_INSTITUTION_SUBSCRIPTION
        ? {
            subscription: {
              startAt: toSubscriptionISODate(input.subscriptionStartAt),
              validUntil: toSubscriptionISODate(input.subscriptionValidUntil),
              plan: input.subscriptionPlan,
              gracePeriodDays: Number(input.subscriptionGracePeriodDays),
              provider: "",
              externalId: "",
            },
          }
        : {}),
    };
    try {
      if (onCreateInstitution) {
        const result = await onCreateInstitution(formattedInput);
        if (!result?.success) {
          throw new Error(result?.message || "Failed to create institution");
        }
        setShowSnackbar(true);
        setSnackbarTitle("Create Successful");
        setSnackbarSubtitle("Institution has been created successfuly");
        setSnackbarType("success");
      } else {
        const response = await APICreateInstitution(
          formattedInput,
          setLoading,
          token
        );
        if (response?.status >= 200 && response?.status < 300) {
          setShowSnackbar(true);
          setSnackbarTitle("Create Successful");
          setSnackbarSubtitle("Institution has been created successfuly");
          setSnackbarType("success");
        } else {
          throw new Error(
            response?.response?.data?.message || "Failed to create institution",
          );
        }
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Create Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      if (!onCreateInstitution) {
        await fetchInstitutions();
        await updateUserPrivileges();
      }
      handleReset();
    }
  };

  const isInstitutionFormValid = () =>
    isStep1Valid() &&
    (ENABLE_INSTITUTION_SUBSCRIPTION ? isStep2Valid() : true) &&
    isStep3Valid();

  const customTripTypeErrors = useMemo(() => {
    const hasSpecialChars = (str) => /[^a-zA-Z0-9 ]/.test(str);
    return (input.customTripTypes || []).map((tripType, index) => {
      const trimmedValue = tripType.trim();
      const isTouched = customTripTypeTouched[index];
      if (trimmedValue === "") {
        return isTouched ? "Trip type cannot be empty" : "";
      }
      if (hasSpecialChars(trimmedValue)) return "Special characters are not allowed";
      return "";
    });
  }, [input.customTripTypes, customTripTypeTouched]);

  useEffect(() => {
    const hasSpecialChars = (str) => /[^a-zA-Z0-9 ]/.test(str);
    const hasSpecialCharsNoSpaces = (str) => /[^a-zA-Z0-9]/.test(str);

    const getIdError = (value) => {
      if (value.length > 60) return "Exceeded character limit";
      if (hasSpecialCharsNoSpaces(value))
        return "Special characters and spaces are not allowed";
      return "";
    };

    const getNameError = (value) => {
      if (value.trim() === "") return "Institution Name cannot be empty";
      if (value.length > 60) return "Exceeded character limit";
      if (hasSpecialChars(value)) return "Special characters are not allowed";
      return "";
    };

    const getSubscriptionStartAtError = (value) => {
      if (value === null || value === undefined || String(value).trim() === "") {
        return "Start date is required";
      }
      return "";
    };

    const getSubscriptionValidUntilError = (startValue, endValue) => {
      const start = startValue ?? "";
      const end = endValue ?? "";

      if (String(end).trim() === "") return "Valid until date is required";
      if (
        String(start).trim() !== "" &&
        dayjs(end).isBefore(dayjs(start), "day")
      ) {
        return "Valid until must be on or after start date";
      }
      return "";
    };

    const getGracePeriodDaysError = (value) => {
      const normalized = String(value ?? "").trim();
      if (normalized === "") return "Grace period is required";
      if (!/^\d+$/.test(normalized)) return "Enter a valid number of days";
      return "";
    };

    setError({
      institutionId:
        input.institutionId !== ""
          ? getIdError(input.institutionId.trim())
          : "",

      institutionName:
        input.institutionName !== ""
          ? getNameError(input.institutionName.trim())
          : "",

      subscriptionStartAt: getSubscriptionStartAtError(input.subscriptionStartAt),

      subscriptionValidUntil: getSubscriptionValidUntilError(
        input.subscriptionStartAt,
        input.subscriptionValidUntil,
      ),

      subscriptionGracePeriodDays: getGracePeriodDaysError(
        input.subscriptionGracePeriodDays,
      ),
    });
  }, [input]);

  useEffect(() => {
    const hasCustomErrors = customTripTypeErrors.some(Boolean);
    setIsSubmitDisabled(!isInstitutionFormValid() || hasCustomErrors);
  }, [input, customTripTypeErrors]);

  const handleAddMore = () => {
    setInput((prev) => ({
      ...prev,
      customTripTypes: [...(prev.customTripTypes || []), ""],
    }));
    setCustomTripTypeTouched((prev) => [...prev, false]);
  };

  const handleCustomTripTypeChange = (index, value) => {
    setInput((prev) => {
      const nextCustomTripTypes = [...(prev.customTripTypes || [])];
      nextCustomTripTypes[index] = value;
      return { ...prev, customTripTypes: nextCustomTripTypes };
    });
    setCustomTripTypeTouched((prev) => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  };

  const handleRemoveCustomTripType = (index) => {
    setInput((prev) => {
      const nextCustomTripTypes = [...(prev.customTripTypes || [])];
      nextCustomTripTypes.splice(index, 1);
      return { ...prev, customTripTypes: nextCustomTripTypes };
    });
    setCustomTripTypeTouched((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const clearSubscriptionStartAtError = () => {
    setError((prev) => ({ ...prev, subscriptionStartAt: "" }));
  };

  const clearSubscriptionValidUntilError = () => {
    setError((prev) => ({ ...prev, subscriptionValidUntil: "" }));
  };

  const handleNextStep = () => {
    if (!isCurrentStepValid() || currentStep >= wizardSteps.length) return;
    setCurrentStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    if (currentStep <= 1) return;
    setCurrentStep((prev) => prev - 1);
  };

  const renderStepIndicator = () => (
    <div className="create-institution-stepper" aria-label="Create institution progress">
      {wizardSteps.map((step, index) => {
        const isActive = currentStep === step.id;
        const isCompleted = currentStep > step.id;
        const isReached = isActive || isCompleted;

        return (
          <div key={step.panel} className="create-institution-stepper-segment">
            <div
              className={`create-institution-step-item${
                isReached ? " active" : ""
              }`}
            >
              <span className="create-institution-step-number">{step.id}</span>
              <span className="create-institution-stepper-label">{step.label}</span>
            </div>
            {index < wizardSteps.length - 1 && (
              <div
                className={`create-institution-step-divider${
                  isCompleted ? " active" : ""
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <Box sx={style} className="create-institution-modal">
        <div className="user-modal-header">
          <div className="user-modal-header-contents">
            <div className="icon-button-container">
              <div className="icon-container">
                <img
                  style={{ margin: "auto" }}
                  src={Institution}
                  alt="invite-users-icon"
                  loading="lazy"
                />
              </div>
              <div className="x-button-container">
                <ModalCloseButton onClick={onClose} className="user-x-button" />
              </div>
            </div>
            <div className="user-modal-header-title">
              <Title
                title="Create Institution"
                subtitle={STEP_SUBTITLES[activePanel]}
                titleSize="1rem"
                subtitleSize="0.875rem"
                gap="0.25rem"
                lineHeight="1.5rem"
              />
            </div>
          </div>
          {renderStepIndicator()}
        </div>
        <div
          ref={contentRef}
          className={`user-modal-content create-institution-wizard ${
            isOverflowing ? "has-overflow" : ""
          }`}
        >
          {activePanel === 1 && (
            <div className="create-institution-step-panel">
              <div className="create-institution-step-field">
                <div className="create-institution-step-field-label">Institution ID</div>
                <TextInput
                  width={FIELD_WIDTH}
                  placeholder="Example: busBuddy"
                  setInput={setInput}
                  input={input}
                  name="institutionId"
                  type="text"
                  error={Boolean(error.institutionId)}
                  setError={setError}
                  errorMessage={error.institutionId}
                  onChangeFunction={handleInputChange}
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                  showCharCount
                  maxLength={60}
                />
              </div>
              <div className="create-institution-step-field">
                <div className="create-institution-step-field-label">Institution Name</div>
                <TextInput
                  width={FIELD_WIDTH}
                  placeholder="Example: School of BusBuddy"
                  setInput={setInput}
                  input={input}
                  name="institutionName"
                  type="text"
                  error={Boolean(error.institutionName)}
                  setError={setError}
                  errorMessage={error.institutionName}
                  onChangeFunction={handleInputChange}
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                  showCharCount
                  maxLength={60}
                />
              </div>
              <div className="create-institution-step-field">
                <div className="create-institution-step-field-label">Timezone</div>
                <Dropdown
                  options={timezoneOptions}
                  name="timezone"
                  placeholder="Example: Asia/Kuala Lumpur"
                  onChangeFunction={handleInputChange}
                  input={input}
                  width={FIELD_WIDTH}
                />
              </div>
            </div>
          )}

          {activePanel === 2 && ENABLE_INSTITUTION_SUBSCRIPTION && (
            <div className="create-institution-step-panel">
              <div className="create-institution-step-field">
                <div className="create-institution-step-field-label">Subscription Start</div>
                <CustomDatePicker
                  placeholder="Select start date"
                  name="subscriptionStartAt"
                  error={Boolean(error.subscriptionStartAt)}
                  setError={clearSubscriptionStartAtError}
                  input={input}
                  setInput={setInput}
                  errorMessage={error.subscriptionStartAt}
                  width={FIELD_WIDTH}
                />
              </div>
              <div className="create-institution-step-field">
                <div className="create-institution-step-field-label">Subscription Valid Until</div>
                <CustomDatePicker
                  placeholder="Select end date"
                  name="subscriptionValidUntil"
                  error={Boolean(error.subscriptionValidUntil)}
                  setError={clearSubscriptionValidUntilError}
                  input={input}
                  setInput={setInput}
                  errorMessage={error.subscriptionValidUntil}
                  width={FIELD_WIDTH}
                  minDate={input.subscriptionStartAt || undefined}
                />
              </div>
              <div className="create-institution-step-field">
                <div className="create-institution-step-field-label">Subscription Plan</div>
                <Dropdown
                  options={SUBSCRIPTION_PLAN_OPTIONS}
                  name="subscriptionPlan"
                  placeholder="Select a plan"
                  onChangeFunction={handleInputChange}
                  input={input}
                  width={FIELD_WIDTH}
                />
              </div>
              <div className="create-institution-step-field create-institution-step-field--trailing">
                <div className="create-institution-step-field-label">Grace Period (days)</div>
                <TextInput
                  width={FIELD_WIDTH}
                  placeholder="Example: 7"
                  setInput={setInput}
                  input={input}
                  name="subscriptionGracePeriodDays"
                  type="text"
                  error={Boolean(error.subscriptionGracePeriodDays)}
                  setError={setError}
                  errorMessage={error.subscriptionGracePeriodDays}
                  onChangeFunction={handleInputChange}
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                />
              </div>
            </div>
          )}

          {activePanel === 3 && (
            <div className="create-institution-step-panel">
              <div className="create-institution-step-field">
                <div className="create-institution-step-field-label">Default Trip Types</div>
                <div className="trip-types-default-container">
                  {DEFAULT_TRIP_TYPES.map((tripType) => (
                    <div key={tripType} className="trip-type-default-pill">
                      {tripType}
                    </div>
                  ))}
                </div>
              </div>
              {(input.customTripTypes || []).length > 0 && (
                <div className="create-institution-step-field">
                  <div className="create-institution-step-field-label">Custom Trip Types</div>
                  <div className="trip-types-extra-container">
                    {(input.customTripTypes || []).map((tripType, index) => {
                      const fieldName = `customTripType-${index}`;
                      return (
                        <div
                          className="trip-type-extra-field"
                          key={`custom-trip-type-${index}`}
                        >
                          <div className="trip-type-extra-input-wrap">
                            <TextInput
                              width="100%"
                              placeholder={`Example: Custom ${index + 1}`}
                              setInput={setInput}
                              input={{ [fieldName]: tripType }}
                              name={fieldName}
                              type="text"
                              error={Boolean(customTripTypeErrors[index])}
                              setError={setError}
                              errorMessage={customTripTypeErrors[index]}
                              onChangeFunction={(e) =>
                                handleCustomTripTypeChange(index, e.target.value)
                              }
                              keyUp={keyUp}
                              setKeyUp={setKeyUp}
                            />
                            <div className="trip-type-extra-remove-row">
                              <TextButton
                                label="Remove"
                                onClickFunction={() =>
                                  handleRemoveCustomTripType(index)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <SecondaryButton
                width="100%"
                label="Add Trip Type"
                onClickFunction={handleAddMore}
              />
            </div>
          )}
        </div>
        <div className="button-container-delete create-institution-wizard-actions">
          {currentStep > 1 ? (
            <SecondaryButton
              width="12rem"
              label="Back"
              imageIcon={false}
              onClickFunction={handlePreviousStep}
            />
          ) : (
            <SecondaryButton
              width="12rem"
              label="Cancel"
              imageIcon={false}
              onClickFunction={handleReset}
            />
          )}
          {currentStep < wizardSteps.length ? (
            <PrimaryButton
              width="12rem"
              label="Next"
              onClickFunction={handleNextStep}
              disable={!isCurrentStepValid()}
            />
          ) : (
            <PrimaryButton
              width="12rem"
              label="Create"
              onClickFunction={handleCreate}
              disable={isSubmitDisabled}
              loading={loading}
            />
          )}
        </div>
      </Box>
    </div>
  );
}
